import { Close, DragIndicator } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Rnd } from 'react-rnd';

import ResumeWarning from './ResumeWarning';

const DEFAULT_POPOVER_DIMENSIONS = {
	width: '400px',
	height: '500px',
};

const DraggablePopover = React.memo(
	({ type, title, showPopover, setShowPopover, children, defaultX, defaultY }) => {
		const { t } = useTranslation(['candidate-feedback']);
		const [position, setPosition] = useState({ x: defaultX, y: defaultY });
		const [size, setSize] = useState({ width: 400, height: 300 });

		const handleKeyDown = (e) => {
			const step = 10;
			switch (e.key) {
				case 'ArrowUp':
					setPosition((prev) => ({ ...prev, y: prev.y - step }));
					break;
				case 'ArrowDown':
					setPosition((prev) => ({ ...prev, y: prev.y + step }));
					break;
				case 'ArrowLeft':
					setPosition((prev) => ({ ...prev, x: prev.x - step }));
					break;
				case 'ArrowRight':
					setPosition((prev) => ({ ...prev, x: prev.x + step }));
					break;
				default:
					break;
			}
		};

		const buttonRef = useRef(null);

		useEffect(() => {
			if (showPopover && buttonRef.current) {
				buttonRef.current.focus();
			}
		}, [showPopover]);

		const handleClose = useCallback(() => {
			setShowPopover(false);
			if (type === 'resume') {
				const resumeButton = document.getElementById('resume-popover-button');
				resumeButton?.focus();
			} else {
				const jobDescButton = document.getElementById('jobDescription-popover-button');
				jobDescButton?.focus();
			}
		}, [setShowPopover, type]);

		useEffect(() => {
			const handleEscape = (e) => {
				if (e.key === 'Escape') {
					handleClose();
				}
			};

			if (showPopover) {
				document.addEventListener('keydown', handleEscape);
			}

			return () => {
				document.removeEventListener('keydown', handleEscape);
			};
		}, [showPopover, setShowPopover, handleClose]);

		const renderDraggablePopover = (content) => (
			<Box
				id={`${type}-popover`}
				open={showPopover}
				sx={{
					background: (theme) => theme.palette.background.paper,
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					overflow: 'hidden',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						borderBottom: 1,
						borderColor: 'divider',
						p: 1,
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<IconButton
							className="modal-drag-handle"
							onKeyDown={handleKeyDown}
							sx={{
								cursor: 'grab',
								color: 'text.secondary',
							}}
							aria-label={
								type === 'resume'
									? t('drag_resume_popover_aria_label')
									: t('drag_job_description_popover_aria_label')
							}
							ref={buttonRef}
						>
							<DragIndicator />
						</IconButton>
						<Typography variant="h2" marginLeft={1}>
							{title}
						</Typography>
					</Box>
					<IconButton
						onClick={handleClose}
						aria-label={
							type === 'resume' ? t('close_resume_popover') : t('close_job_description_popover')
						}
						sx={{ color: 'text.secondary' }}
						id={'close-popover-button'}
					>
						<Close />
					</IconButton>
				</Box>
				<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
					{content}
				</Box>
			</Box>
		);
		return (
			<Rnd
				position={position}
				size={size}
				default={{
					x: defaultX,
					y: defaultY,
					width: DEFAULT_POPOVER_DIMENSIONS.width,
					height: DEFAULT_POPOVER_DIMENSIONS.height,
				}}
				bounds="window"
				dragHandleClassName="modal-drag-handle"
				style={{ background: 'white', borderRadius: '8px', boxShadow: '0 0 10px rgba(0,0,0,0.3)' }}
				onDragStop={(e, d) => setPosition({ x: d.x, y: d.y })}
				// eslint-disable-next-line id-match
				onResizeStop={(e, direction, ref, delta, _position) => {
					setSize({
						width: parseInt(ref.style.width, 10),
						height: parseInt(ref.style.height, 10),
					});
					setPosition(_position);
				}}
			>
				<Suspense fallback={<CircularProgress />}>
					{renderDraggablePopover(
						<>
							{type === 'resume' && <ResumeWarning />}
							<Box
								sx={{
									overflowY: 'auto',
									width: '100%',
									height: '100%',
								}}
							>
								{children}
							</Box>
						</>,
					)}
				</Suspense>
			</Rnd>
		);
	},
);

DraggablePopover.displayName = 'DraggablePopover';
export default DraggablePopover;
